import { state } from '../map_state/state'
import { Renderer } from '../renderer'
import { viewport } from '../map_state/viewport'
import { callMethodOnKeydown, screenCssToWorld, isTyping } from '../helpers'
import { panTool } from './pan_tool'
import { addSensorTool } from './add_sensor_tool'
import { cropTool } from './crop_tool'
import { pointDistanceTool } from './point_distance_tool'
import { dragTool } from './drag_tool'
import { pathTracerTool } from './path_tracer_tool'
import { historyTool } from './history_tool'

class LinearFlowTool {
  constructor () {
    this.element = document.getElementById('mct-linear-flow')
    this.keys = {
      Escape: 'Remove All Flows',
      Backspace: 'Clear current pending flow start'
    }
    this.toggleVisibility()
    this.bindEvents()
  }

  bindEvents () {
    this.onKeyDown = this.onKeyDown.bind(this)
    this.activate = this.activate.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerLeave = this.onPointerLeave.bind(this)

    this.element.addEventListener('click', this.activate)
    document.addEventListener('keydown', this.onKeyDown)
  }

  unBindEvents () {
    this.element.removeEventListener('click', this.activate)
    document.removeEventListener('keydown', this.onKeyDown)
    viewport.canvas.removeEventListener('click', this.onClick)
    viewport.canvas.removeEventListener('pointermove', this.onPointerMove)
    viewport.canvas.removeEventListener('pointerleave', this.onPointerLeave)
  }

  toggleVisibility () {
    this.element.classList.toggle('hidden', !state.mapImage || state.sensors.length < 1)
  }

  onKeyDown (e) {
    if (isTyping(e.target)) return
    if (e.key === 'l' && !this.element.classList.contains('hidden')) {
      callMethodOnKeydown(e.target, this.activate)
    }

    if (e.key === 'Backspace' && state.linearFlowMode) {
      state.flowStartSensorId = null
      state.flowPreviewWorld = null
      Renderer.render()
    }

    if (e.key === 'Escape' && state.linearFlowMode) {
      state.sensors.forEach(sensor => sensor.flowEndSensorIds = [])
      historyTool.add()
      Renderer.render()
    }
  }

  activate (mode = true) {
    if (!state.mapImage) return

    const active = !state.linearFlowMode && mode

    if (active) {
      if (state.panMode) panTool.activate(false)
      else if (state.addSensorMode) addSensorTool.activate(false)
      else if (state.cropMode) cropTool.activate(false)
      else if (state.dragMode) dragTool.activate(false)
      else if (state.tracerMode) pathTracerTool.enableMode(state.tracerMode, false)
      if (state.pointDistanceMode) pointDistanceTool.activate(false)
      state.linearFlowMode = true
      this.element.classList.add('selected')
      viewport.canvas.addEventListener('click', this.onClick)
      viewport.canvas.addEventListener('pointermove', this.onPointerMove)
      viewport.canvas.addEventListener('pointerleave', this.onPointerLeave)
    } else {
      state.linearFlowMode = false
      state.flowStartSensorId = null
      state.flowPreviewWorld = null
      this.element.classList.remove('selected')
      viewport.canvas.removeEventListener('click', this.onClick)
      viewport.canvas.removeEventListener('pointermove', this.onPointerMove)
      viewport.canvas.removeEventListener('pointerleave', this.onPointerLeave)
    }

    Renderer.render()
  }

  onPointerMove (e) {
    if (!state.linearFlowMode || !state.flowStartSensorId) return

    const rect = viewport.canvas.getBoundingClientRect()
    state.flowPreviewWorld = screenCssToWorld({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })

    Renderer.render()
  }

  onPointerLeave () {
    if (!state.linearFlowMode) return
    state.flowPreviewWorld = null
    Renderer.render()
  }

  onClick (e) {
    if (!state.linearFlowMode) return

    const rect = viewport.canvas.getBoundingClientRect()

    const world = screenCssToWorld({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })

    const hit = this.findSensorUnderCursor(world)
    if (!hit) return

    if (!state.flowStartSensorId) {
      state.flowStartSensorId = hit.id
      state.selectedSensorId = hit.id
      state.flowPreviewWorld = hit.position
      Renderer.render()
      return
    }

    const startSensor = state.sensors.find(s => String(s.id) === String(state.flowStartSensorId))
    if (!startSensor) {
      state.flowStartSensorId = hit.id
      state.selectedSensorId = hit.id
      state.flowPreviewWorld = hit.position
      historyTool.add()
      Renderer.render()
      return
    }

    if (String(startSensor.id) === String(hit.id)) {
      state.flowStartSensorId = null
      state.flowPreviewWorld = null
      historyTool.add()
      Renderer.render()
      return
    }

    startSensor.flowEndSensorIds ??= []

    const alreadyLinked = startSensor.flowEndSensorIds.some(id => String(id) === String(hit.id))
    if (!alreadyLinked) {
      startSensor.flowEndSensorIds.push(hit.id)
    }

    state.selectedSensorId = startSensor.id
    state.flowPreviewWorld = hit.position

    historyTool.add()
    Renderer.render()
  }

  findSensorUnderCursor (world) {
    let best = null
    let bestDistance = Infinity
    const hitRadius = 12 / state.zoomValue

    for (const sensor of state.sensors) {
      const d = Math.hypot(world.x - sensor.position.x, world.y - sensor.position.y)
      if (d <= hitRadius && d < bestDistance) {
        best = sensor
        bestDistance = d
      }
    }

    return best
  }
}

export let linearFlowTool = null

export function initLinearFlowTool () {
  linearFlowTool = new LinearFlowTool()
  return linearFlowTool
}
